import React from 'react';
import { actionColumnDefinition, booleanColumnDefinition, ColumnDefinition, currencyColumnDefinition, numericColumnDefinition, textColumnDefinition } from '../table/ColumnDefinition';
import { ITransaction } from '../../requests/ITransactionWebModelRequest';
import { UtilService } from '../../services/UtilService';
import { WebCache } from '../../services/WebCache';
import { TFunction } from 'react-i18next';
import { ReactComponent as TicketIMG } from 'itd-common/assets/images/transactions/Receipt Slip.svg';
import TarjetaLogo from 'itd-common/assets/images/issuers/PCT-logo.svg';
import i18next from 'i18next';
import { TransactionService } from '../../services/TransactionService';
import { CurrencyHelper } from '../../helpers/CurrencyHelper';

interface TransactionColumnsProps {
    t: TFunction;
    processAction: (event: any, transaction: ITransaction) => void;
    getTicket?: (tranId: string, transactionType: string) => void; 
    showCouponColumn?:boolean;
    setRowDetail?: (rowDetail: RowDetailType | null) => void;
}

type BuildTotalDetailOptions = { object: any, title: string, accumulateTotal: boolean };

interface RowDetailType {
    title: string;
    total: string;
    data: Array<{ prop: string; value: string }>;
}

export const TransactionColumns = ({ t, processAction, getTicket, showCouponColumn, setRowDetail }: TransactionColumnsProps): ColumnDefinition[] => {
    const T_PREFIX = 'pages.reportTransactions.';

    const showTotalRefund = (rowObject: ITransaction, detailOptions: BuildTotalDetailOptions): void => {
            if (detailOptions && setRowDetail) {
                const currencySymbol: string = CurrencyHelper.getCurrencySymbol(rowObject.currency);
                setRowDetail(TransactionService.buildTotalDetail(detailOptions.object, detailOptions.title, currencySymbol, detailOptions.accumulateTotal));
            }
    };

    const showTotalRefundAction = (rowObject: ITransaction): void => {
        var o: any = {};
        let label = i18next.t('relatedTransactions');
        let accumulateTotal = true;
        if (rowObject.transactionType == "D") {
            label = i18next.t('relatedSale');
            accumulateTotal = false;
        }
        for (var i in rowObject.relatedTransactions) {
            o[rowObject.relatedTransactions[i].transactionReference] = Number.parseInt(rowObject.relatedTransactions[i].amount) / 100;
        }
        showTotalRefund(rowObject, { object: o, title: label, accumulateTotal: accumulateTotal });
    };

    var couponTransactionTypes = WebCache.isUserUYU() ? ['C', 'A', 'L', 'D', 'T', 'U', 'H'] : ['C', 'A', 'L', 'D' ];
    return [
        showCouponColumn
            ? actionColumnDefinition({
            key: 'coupon',
            label: t(`${T_PREFIX}transactionsColumn.coupon`),
            header: true,
            action: {
                function: (rowObject: ITransaction) => {
                    if (
                        getTicket && 
                        rowObject.acquirer !== '0700' &&
                        couponTransactionTypes.includes(rowObject.transactionType) &&
                        ['00', '08', '11', '85', 'OF'].includes(rowObject.responseCode)
                    ) {
                        return getTicket(rowObject.id, rowObject.transactionType);
                    } else {
                        return '-';
                        }
                    },
                icon: (rowObject: ITransaction) => {
                    if (
                        getTicket && 
                        rowObject.acquirer !== '0700' &&
                        couponTransactionTypes.includes(rowObject.transactionType) &&
                        ['00', '08', '11', '85', 'OF'].includes(rowObject.responseCode)
                    ) {
                        return <TicketIMG />;
                    } else {
                        return '-';
                    }
                },
            },
        })
        : textColumnDefinition({ key: '', label: '' }),
        textColumnDefinition({ key: 'authDateTime', label: t(`${T_PREFIX}transactionsColumn.authDateTime`),header:true,mapValue:(rowObject:ITransaction)=>{
            const authDate= rowObject.authDateTime
            return UtilService.getDDMMyyHHmmssFormatFromString(authDate.toString())
        }}),
        textColumnDefinition({ key: 'terminal', label: t(`${T_PREFIX}transactionsColumn.terminal`),header:true,mapValue:(rowObject:ITransaction)=>{
            const terminal = rowObject.terminal 
            if (terminal.toString().includes("B24_")){
                return terminal?t(terminal).substring(4):"-"
            } else {
                return terminal?t(terminal):"-"
            }
        }}),
        textColumnDefinition({ key: 'transactionType', label: t(`${T_PREFIX}transactionsColumn.transactionType`),header:true,mapValue:(rowObjects:ITransaction)=>{
            const transactionType = rowObjects.transactionType
            return transactionType ? t("transactionType" + "-" +transactionType):"-"
        }}),
        numericColumnDefinition({ key: 'state', label: t(`${T_PREFIX}transactionsColumn.state`),header:true,mapValue:(rowObjects:ITransaction)=>{
            const state = rowObjects.state
            return state ? t("state" + "-" +state):"-"
        }}),
        !WebCache.isUserARG() ? textColumnDefinition({ key: 'acquirer', label: t(`${T_PREFIX}transactionsColumn.acquirer`),header:true,mapValue:(rowObject:ITransaction)=>{
            const acquirer = rowObject.acquirer;
            if (acquirer === "0000") {
                return "-";
            } else {
                let value = t("acquirer" + "-" + acquirer).toUpperCase();
                let image = UtilService.getImageTag(value);
            
                if (value.indexOf("EDENRED") >= 0) {
                    value = " " + value.replace("EDENRED", "").trim().toLowerCase();
                } else if (value.indexOf("CABAL") >= 0) {
                    value = " " + value.replace("CABAL", "").trim().toLowerCase();
                } else if (value.indexOf("CREDITEL") >= 0) {
                    value = " " + value.replace("CREDITEL", "").trim().toLowerCase();
                } else if (value.indexOf("PASS CARD") >= 0) {
                    value = " " + value.replace("PASS CARD", "").trim().toLowerCase();
                } else {
                    value = "";
                }
                return (
                    <div>
                        <img src={image} alt={`${acquirer}`} />
                    </div>
                );
            }
        }}):
            textColumnDefinition({ key: 'ticket', label: t(`${T_PREFIX}transactionsColumn.ticket`),header:true,mapValue:(rowObject:ITransaction)=>{
                    const ticket = rowObject.ticket
                    return ticket || "-"
                }})
        ,
        textColumnDefinition({
            key: 'issuer',
            label: t(`${T_PREFIX}transactionsColumn.issuer`),
            header: true,
            mapValue: (rowObject: ITransaction) => {
                const issuer = rowObject.issuer;
                const { value: displayValue, image: imageSrc } = UtilService.getIssuerGridValueInt(issuer, "issuer", t);
                if (rowObject.inputMode === "QRFD") {
                    return <img src={TarjetaLogo} alt={'PCT'} />;
                }else if(rowObject.issuer === "VISA-N/A-ARV7VISA"){
                    return <img src={imageSrc} alt={'VISA'} />;
                } else {
                    if(imageSrc!=null&&imageSrc.length>0)
                        return (
                            issuer?
                            <div>
                                <img src={imageSrc} alt={displayValue} />
                                <span>{displayValue}</span>
                            </div>:"-"
                        );
                    else
                        return (
                            issuer?
                                <div>
                                    <span>{displayValue}</span>
                                </div>:"-"
                        );
                }
            },
        }),
        WebCache.isUserARG() ? 
        textColumnDefinition({ key: 'walletProductId', label: t(`${T_PREFIX}transactionsColumn.walletProductId`), header: true, mapValue: (rowObject: ITransaction) => {
            const wallet = rowObject.walletProductId;
            return wallet?<img src={UtilService.getImageTag(t("issuer" + "-" + wallet))} alt={`${wallet}`} />:"-";
        }}) :
        textColumnDefinition({ key: '', label: '' }),
        textColumnDefinition({ key: 'responseCode', label: t(`${T_PREFIX}transactionsColumn.responseCode`),header:true,mapValue:(rowObject:ITransaction)=>{
            const responseCode = rowObject.responseCode
            if (responseCode === "00" || responseCode === "08"|| responseCode === "11"|| responseCode === "85"|| responseCode === "LV"|| responseCode === "OF"){
                return <div className='response-code-success'>{t("responseCode"+"-"+responseCode)}</div>
            } else if (responseCode === "01" || responseCode === "08"|| responseCode === "11"|| responseCode === "85"|| responseCode === "LV"|| responseCode === "OF"){
                return <div className='response-code-warning'>{t("responseCode"+"-"+responseCode)}</div>
            } else {
                return <div className='response-code-error'>{t("responseCode"+"-"+responseCode)}</div>
            }
        }}),
        WebCache.isUserARG()? 
        currencyColumnDefinition({ key: 'amountToCustomer', label: t(`${T_PREFIX}transactionsColumn.totalAmountForReport`),columnDefinitionKey:'currency',header:true,mapValue:(rowObject:ITransaction)=>{
            const amountToCustomer = rowObject.amountToCustomer
            return amountToCustomer?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(amountToCustomer)):"-"
        }}):       
        currencyColumnDefinition({ key: 'totalAmountForReport', label: t(`${T_PREFIX}transactionsColumn.totalAmountForReport`),columnDefinitionKey:'currency',header:true,mapValue:(rowObject:ITransaction)=>{
            const totalAmountForReport = rowObject.totalAmountForReport
            if (rowObject.transactionType === "L"){
                return <div className='d-flex align-items-center' onClick={(event: any) => processAction(event, rowObject)}><div>{ Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(totalAmountForReport))}</div> <div className='fa-solid fa-magnifying-glass text-primary'></div></div>
            } else {
                return <div>{ Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(totalAmountForReport))}</div>
            }
        }}), 
        booleanColumnDefinition({ key: 'isClosed', label: t(`${T_PREFIX}transactionsColumn.isClosed`),header:false,mapValue:(rowObject:ITransaction)=>{
            let isClosed:boolean = rowObject.isClosed
            if (isClosed){
                return <div className='success-icon fa fa-check-circle-o'></div>
            } else {
                return "-"
            }
        }}),
        textColumnDefinition({ key: 'id', label: t(`${T_PREFIX}transactionsColumn.id`),header:false,mapValue:(rowObject:ITransaction)=>{
            const id = rowObject.id
            return id || "-"
        }}),
        textColumnDefinition({ key: 'authorizationCode', label: t(`${T_PREFIX}transactionsColumn.authorizationCode`),header:false,mapValue:(rowObject:ITransaction)=>{
            const authorizationCode = rowObject.authorizationCode
            return authorizationCode || "-"
        }}),
        textColumnDefinition({ key: 'currency', label: t(`${T_PREFIX}transactionsColumn.currency`),header:false,mapValue:(rowObject:ITransaction)=>{
            const currency = rowObject.currency
            return t("currency"+"-"+currency)
        }}),
        WebCache.isUserARG()?
        textColumnDefinition({ key: 'totalAmountForReport', label: t(`${T_PREFIX}transactionsColumn.amount`),header:false,mapValue:(rowObject:ITransaction)=>{
            const totalAmountForReport = rowObject.totalAmountForReport
            return totalAmountForReport?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(totalAmountForReport)):"-"
        }})
        :
        textColumnDefinition({ key: 'amountToCustomer', label: t(`${T_PREFIX}transactionsColumn.amountToCustomer`),header:false,mapValue:(rowObject:ITransaction)=>{
            const amountToCustomer = rowObject.amountToCustomer
            return amountToCustomer?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(amountToCustomer)):"-"
        }}),
        textColumnDefinition({ key: 'merchant', label: t(`${T_PREFIX}transactionsColumn.merchant`),header:false,mapValue:(rowObject:ITransaction)=>{
            const merchant = rowObject.merchant
            return merchant || "-"
        }}),
        textColumnDefinition({ key: 'authTerminal', label: t(`${T_PREFIX}transactionsColumn.authTerminal`),header:false,mapValue:(rowObject:ITransaction)=>{
            const authTerminal = rowObject.authTerminal
            return authTerminal || "-"
        }}),
        currencyColumnDefinition({ key: 'tipAmountForReport', label: t(`${T_PREFIX}transactionsColumn.tipAmountForReport`),columnDefinitionKey:'currency',header:false,mapValue:(rowObject:ITransaction)=>{
            const tipAmountForReport = rowObject.tipAmountForReport
            return tipAmountForReport?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(tipAmountForReport)):"-"
        }}),     
        currencyColumnDefinition({ key: 'cashbackAmountForReport', label: t(`${T_PREFIX}transactionsColumn.cashbackAmount`),columnDefinitionKey:'currency',header:false,mapValue:(rowObject:ITransaction)=>{
            const cashbackAmountForReport = rowObject.cashbackAmountForReport
            return cashbackAmountForReport?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(cashbackAmountForReport)):"-"
        }}),
        !WebCache.isUserARG()?
        textColumnDefinition({ key: 'ticket', label: t(`${T_PREFIX}transactionsColumn.ticket`),header:false,mapValue:(rowObject:ITransaction)=>{
            const ticket = rowObject.ticket
            return ticket || "-"
        }}):textColumnDefinition({ key: '', label:"",header:false}),
        currencyColumnDefinition({ key: 'taxAmountForReport', label: t(`${T_PREFIX}transactionsColumn.taxAmountForReport`),columnDefinitionKey:'currency',header:false,mapValue:(rowObject:ITransaction)=>{
            const taxAmountForReport = rowObject.taxAmountForReport
            return taxAmountForReport?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(taxAmountForReport)):"-"
        }}),
        textColumnDefinition({ key: 'quotas', label: t(`${T_PREFIX}transactionsColumn.quotas`),header:false,mapValue:(rowObject:ITransaction)=>{
            const quotas = rowObject.quotas;
            const issuer = rowObject.issuer;
            
            if (quotas == "11" && issuer == "0102") {
                return "Plan Z"
            }
            return quotas || "-";
        }}),
        textColumnDefinition({ key: 'billNumber', label: t(`${T_PREFIX}transactionsColumn.billNumber`),header:false,mapValue:(rowObject:ITransaction)=>{
            const billNumber = rowObject.billNumber
            return billNumber?billNumber.replace(/^0+/ ,''):"-"
        }}),
        textColumnDefinition({ key: 'taxRefund', label: t(`${T_PREFIX}transactionsColumn.taxRefund`),header:false,mapValue:(rowObjects:ITransaction)=>{
            const taxRefund = rowObjects.taxRefund
            if ((taxRefund + "").startsWith("0#")) {
                return t("taxRefund-0");
            } else {
                const fields = ("" + taxRefund).split("#");
                if (fields.length > 0) {
                    const key = fields.length > 1 && fields[1].trim().length > 0 
                        ? fields[1].trim() 
                        : fields[0].trim();
                    const translationKey = `taxRefund-${key}`;
                    
                    if (translationKey.startsWith("taxRefund-")) {
                        return "-";
                    }
                    return t(translationKey);
                }
                return "-";
            }
        }}),

        textColumnDefinition({ key: 'batch', label: t(`${T_PREFIX}transactionsColumn.batch`),header:false,mapValue:(rowObject:ITransaction)=>{
            const batch = rowObject.batch
            return batch || "-"
        }}),
        textColumnDefinition({ key: 'additionalData', label: t(`${T_PREFIX}transactionsColumn.additionalData`),header:false,mapValue:(rowObject:ITransaction)=>{
            const additionalData = rowObject.additionalData
            return additionalData || "-"
        }}),
        textColumnDefinition({ key: 'cardNumber', label: t(`${T_PREFIX}transactionsColumn.cardNumber`),header:false,mapValue:(rowObject:ITransaction)=>{
            const cardNumber = rowObject.cardNumber
            return cardNumber || "-"
        }}),
        textColumnDefinition({ key: 'expirationDate', label: t(`${T_PREFIX}transactionsColumn.expirationDate`),header:false,mapValue:(rowObject:ITransaction)=>{
            const expirationDate = rowObject.expirationDate
            return expirationDate || "-"
        }}),
        textColumnDefinition({ key: 'inputMode', label: t(`${T_PREFIX}transactionsColumn.inputMode`),header:false,mapValue:(rowObjects:ITransaction)=>{
            const inputMode = rowObjects.inputMode
            return inputMode?t("inputMode"+"-"+ inputMode):"-"
        }}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'originalTicket', label: t(`${T_PREFIX}transactionsColumn.originalTicket`),header:false,mapValue:(rowObject:ITransaction)=>{
            const originalTicket = rowObject.originalTicket
            return originalTicket || "-"
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'originalDate', label: t(`${T_PREFIX}transactionsColumn.originalDate`),header:false,mapValue:(rowObject:ITransaction)=>{
            const originalDate = rowObject.originalDate
            return originalDate || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'emvApplicationName', label: t(`${T_PREFIX}transactionsColumn.emvApplicationName`),header:false,mapValue:(rowObject:ITransaction)=>{
            const emvApplicationName = rowObject.emvApplicationName
            return emvApplicationName || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'emvApplicationId', label: t(`${T_PREFIX}transactionsColumn.emvApplicationId`),header:false,mapValue:(rowObject:ITransaction)=>{
            const emvApplicationId = rowObject.emvApplicationId
            return emvApplicationId || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'accountType', label: t(`${T_PREFIX}transactionsColumn.accountType`),header:false,mapValue:(rowObject:ITransaction)=>{
            const accountType = rowObject.accountType;
            if (accountType && accountType.length > 0) { 
                return t("accountType" + "-" + accountType);
            }
            return "-";
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'cardOwnerName', label: t(`${T_PREFIX}transactionsColumn.cardOwnerName`),header:false,mapValue:(rowObject:ITransaction)=>{
            const cardOwnerName = rowObject.cardOwnerName
            return cardOwnerName || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?currencyColumnDefinition({ key: 'billAmountForReport', label: t(`${T_PREFIX}transactionsColumn.billAmountForReport`),columnDefinitionKey:'currency',header:false,mapValue:(rowObject:ITransaction)=>{
            const billAmountForReport = rowObject.billAmountForReport
            return billAmountForReport?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(billAmountForReport)):"-"
        }}):textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?currencyColumnDefinition({ key: 'taxableAmountForReport', label: t(`${T_PREFIX}transactionsColumn.taxableAmountForReport`),columnDefinitionKey:'currency',header:false,mapValue:(rowObjects:ITransaction)=>{
            const taxableAmountForReport = rowObjects.taxableAmountForReport
            return taxableAmountForReport?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObjects.currency=="840"?"USD":"UYU"}).format(Number(taxableAmountForReport)):"-"
        }}):textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'originCardType', label: t(`${T_PREFIX}transactionsColumn.originCardType`),header:false,mapValue:(rowObject:ITransaction)=>{
            const originCardType = rowObject.originCardType
            return originCardType || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'initQuestion', label: t(`${T_PREFIX}transactionsColumn.initQuestion`),header:false,mapValue:(rowObject:ITransaction)=>{
            const initQuestion = rowObject.initQuestion
            return initQuestion || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==2?textColumnDefinition({ key: 'terminalGroup', label: t(`${T_PREFIX}transactionsColumn.terminalGroup`),header:false,mapValue:(rowObject:ITransaction)=>{
            const terminalGroup = rowObject.terminalGroup
            return terminalGroup || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        textColumnDefinition({ key: 'clientDocument', label: WebCache.getCurrentUser()?.countryId==2 ?t(`${T_PREFIX}transactionsColumn.clientDocumentUy`):t(`${T_PREFIX}transactionsColumn.clientDocumentArg`),header:false}),
        textColumnDefinition({ key: 'address', label: t(`${T_PREFIX}transactionsColumn.address`),header:false,mapValue:(rowObject:ITransaction)=>{
            const address = rowObject.address
            return address || "-"        
        }}),
        textColumnDefinition({ key: 'location', label: t(`${T_PREFIX}transactionsColumn.location`),header:false,mapValue:(rowObject:ITransaction)=>{
            const location = rowObject.location
            return location || "-"        
        }}),
        textColumnDefinition({ key: 'department', label: WebCache.getCurrentUser()?.countryId==2?t(`${T_PREFIX}transactionsColumn.departamentUy`):t(`${T_PREFIX}transactionsColumn.departamentArg`),header:false}),
        textColumnDefinition({ key: 'wholesalerName', label: t(`${T_PREFIX}transactionsColumn.wholesalerName`),header:false,mapValue:(rowObject:ITransaction)=>{
            const wholesalerName = rowObject.wholesalerName
            return wholesalerName || "-"        
        }}),
        textColumnDefinition({ key: 'specialData', label: t(`${T_PREFIX}transactionsColumn.specialData`),header:false,mapValue:(rowObject:ITransaction)=>{
            const specialData = rowObject.specialData
            return specialData || "-"        
        }}),
        textColumnDefinition({ key: 'userId', label: t(`${T_PREFIX}transactionsColumn.userId`),header:false,mapValue:(rowObject:ITransaction)=>{
            const userId = rowObject.userId
            return userId || "-"        
        }}),
        WebCache.getCurrentUser()?.countryId==1?textColumnDefinition({ key: 'muId', label: t(`${T_PREFIX}transactionsColumn.muId`),header:false,mapValue:(rowObject:ITransaction)=>{
            const muId = rowObject.muId
            return muId || "-"        
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==1?actionColumnDefinition({key: 'relatedTransactions', label: 'transactionsVinculated',header:false, action: { 
            function: showTotalRefundAction.bind(this), 
            icon:'fa-solid fa-magnifying-glass'
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
        WebCache.getCurrentUser()?.countryId==1?textColumnDefinition({ key: 'refundBalance', label: t(`${T_PREFIX}transactionsColumn.refundBalance`),header:false,mapValue:(rowObject:ITransaction)=>{
            const refundBalance = rowObject.refundBalance
            return refundBalance?Intl.NumberFormat('es-UY', {style: 'currency',currency: rowObject.currency=="840"?"USD":"UYU"}).format(Number(refundBalance)):"-"
        }})
        :textColumnDefinition({ key: '', label:"",header:false}),
    ];
};
